import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { Preferences } from '@capacitor/preferences';
import { TokenService } from '../servicios/token.service';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  // Timeouts definidos en especificación:
  // 15s estándar, 30s para descargas/facturación/PDFs
  private readonly DEFAULT_TIMEOUT_MS = 15000;
  private readonly EXTENDED_TIMEOUT_MS = 30000;

  constructor(
    private tokenService: TokenService,
    private router: Router,
    private toastController: ToastController
  ) {}

  private async showSessionExpiredToast() {
    try {
      const toast = await this.toastController.create({
        message: 'Tu sesión ha expirado. Por favor ingresa nuevamente.',
        duration: 3500,
        position: 'bottom',
        color: 'warning'
      });
      await toast.present();
    } catch {}
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let authReq = req;
    const isLoginEndpoint = req.url.includes('/auth/login');

    // 1. Inyectar Authorization: Bearer <token> en todas las peticiones excepto login
    if (!isLoginEndpoint) {
      const token = this.tokenService.getToken();
      if (token) {
        authReq = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${token}`)
        });
      }
    }

    // 2. Determinar timeout según el tipo de solicitud
    const isHeavyRequest = req.url.includes('/facturacion') ||
                           req.url.includes('/pdf') ||
                           req.url.includes('/descargas');
    const timeoutDuration = isHeavyRequest ? this.EXTENDED_TIMEOUT_MS : this.DEFAULT_TIMEOUT_MS;

    return next.handle(authReq).pipe(
      timeout(timeoutDuration),
      catchError((error: any) => {
        if (error instanceof TimeoutError) {
          console.error(`[AuthInterceptor] Petición abortada por timeout (${timeoutDuration}ms):`, req.url);
          return throwError(() => new Error('Tiempo de espera agotado. Por favor, intenta de nuevo.'));
        }

        if (error instanceof HttpErrorResponse) {
          // 3. Manejo de Errores 401 y 403: Purgar almacenamiento y redirigir a /login (excepto en rutas públicas o el mismo login)
          if ((error.status === 401 || error.status === 403) && !isLoginEndpoint && !req.url.includes('/validar-curp')) {
            console.warn(`[AuthInterceptor] Acceso no autorizado (${error.status}). Purgando sesión y redirigiendo.`);
            Preferences.remove({ key: 'jwt_token' }).catch(() => {});
            this.tokenService.logOut();
            this.router.navigate(['/login']);
            this.showSessionExpiredToast();
          }
        }

        return throwError(() => error);
      })
    );
  }
}

export const authInterceptorProvider = [
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];
